import { firstDayOfWeek } from '@kidshome/i18n';

/**
 * Local-date helpers for the family app. Task screens query by a household-local `YYYY-MM-DD` (the
 * backend stores occurrences against a local date, not an instant — ADR-0023), so we derive the
 * civil date in the household timezone via `Intl`, never `Date#toISOString` (which is UTC).
 */

/** The civil date (`YYYY-MM-DD`) for `instant` in `timeZone` (no UTC drift). */
export function localDateISO(timeZone: string, instant: Date = new Date()): string {
  // `en-CA` formats as YYYY-MM-DD; `timeZone` pins the civil day.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instant);
}

/**
 * The UTC offset (in minutes) of `timeZone` at the given UTC `instant` — positive east of UTC
 * (e.g. +120 for Europe/Kyiv in summer). Derived by formatting the instant's civil wall-clock in the
 * zone and differencing it against the same wall-clock read as UTC; correct across DST.
 */
function tzOffsetMinutes(timeZone: string, instant: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);
  const get = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? '0');
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );
  return Math.round((asUtc - instant.getTime()) / 60_000);
}

/**
 * The UTC instant (ISO 8601) for a household-LOCAL wall-clock day + time-of-day. A history/feed date
 * filter bound is a civil day in the household timezone (ADR-0023), not the browser's — so a
 * `YYYY-MM-DD` plus `timeOfDay` ("00:00:00" for a from-bound, "23:59:59.999" for a to-bound) is
 * resolved against `timeZone`'s offset at that day (correct across DST), never browser-local midnight.
 */
export function localDateTimeToISO(timeZone: string, iso: string, timeOfDay: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const [hh = 0, mm = 0, rest = '0'] = timeOfDay.split(':');
  const [ss = 0, ms = 0] = rest.split('.').map(Number);
  // First read the wall-clock as if it were UTC, then correct by the zone's offset at that moment.
  const naiveUtc = Date.UTC(
    y ?? 1970,
    (m ?? 1) - 1,
    d ?? 1,
    Number(hh),
    Number(mm),
    Number(ss),
    Number(ms),
  );
  const offset = tzOffsetMinutes(timeZone, new Date(naiveUtc));
  return new Date(naiveUtc - offset * 60_000).toISOString();
}

/** Parse a `YYYY-MM-DD` to a UTC-noon Date (stable for display formatting, no TZ rollover). */
export function parseLocalDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1, 12, 0, 0));
}

/** Add `days` to a `YYYY-MM-DD`, returning a new `YYYY-MM-DD`. */
export function addDaysISO(iso: string, days: number): string {
  const date = parseLocalDate(iso);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/**
 * The `YYYY-MM-DD` of the week containing `iso`, using the locale's first day of week (CLDR;
 * Mon for uk, Sun/Mon for en). Used as `weekStart` for the agenda query.
 */
export function weekStartISO(iso: string, locale: string): string {
  const date = parseLocalDate(iso);
  const isoDow = ((date.getUTCDay() + 6) % 7) + 1; // 1=Mon..7=Sun
  const first = firstDayOfWeek(locale); // 1=Mon..7=Sun
  const delta = (isoDow - first + 7) % 7;
  return addDaysISO(iso, -delta);
}
